import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Users, Plus, Trash2 } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import api from '../../api/client';

const RELATION_TYPES = [
  { value: 'case_involvement', label: 'Case Involvement' },
  { value: 'employment',       label: 'Employment' },
];

const inputClass = 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';
const cellInputClass = 'flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500';

export default function PartyRolesManager() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRole, setNewRole] = useState({
    relation_type: 'case_involvement',
    role_name: '',
    applies_to_contact_type: '',
    sort_order: 0,
  });

  const loadRoles = useCallback(async () => {
    try {
      const res = await api.get('/admin/party-roles');
      setRoles(res.data?.roles || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load party roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const handleAdd = async () => {
    if (!newRole.role_name.trim()) {
      toast.error('Role name required');
      return;
    }
    try {
      await api.post('/admin/party-roles', {
        ...newRole,
        applies_to_contact_type: newRole.applies_to_contact_type || null,
      });
      toast.success('Role added');
      setNewRole({ relation_type: 'case_involvement', role_name: '', applies_to_contact_type: '', sort_order: 0 });
      loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add role');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this role?')) return;
    try {
      await api.delete(`/admin/party-roles/${id}`);
      toast.success('Role deactivated');
      loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate');
    }
  };

  const handleUpdate = async (role, changes) => {
    try {
      await api.put(`/admin/party-roles/${role.id}`, { ...role, ...changes });
      toast.success('Updated');
      loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const grouped = roles.reduce((acc, r) => {
    if (!acc[r.relation_type]) acc[r.relation_type] = [];
    acc[r.relation_type].push(r);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto">
      <AdminPageHeader
        title="Party Roles"
        description="Tenant-configurable role list for parties on cases and employees on employer companies"
        icon={Users}
        iconColor="text-indigo-600"
        iconBg="bg-indigo-50"
      />

      {/* Add new role */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Role</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            value={newRole.relation_type}
            onChange={(e) => setNewRole({ ...newRole, relation_type: e.target.value })}
            className={inputClass}
          >
            {RELATION_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
          </select>
          <input
            type="text"
            placeholder="Role name (e.g. Attorney of Record)"
            value={newRole.role_name}
            onChange={(e) => setNewRole({ ...newRole, role_name: e.target.value })}
            className={inputClass}
          />
          <input
            type="text"
            placeholder="Filter by contact_type (optional)"
            value={newRole.applies_to_contact_type}
            onChange={(e) => setNewRole({ ...newRole, applies_to_contact_type: e.target.value })}
            className={inputClass}
          />
          <button
            onClick={() => handleAdd()}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      </div>

      {/* Role lists */}
      {RELATION_TYPES.map(rt => (
        <div key={rt.value} className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{rt.label}</h2>
          {(grouped[rt.value] || []).length === 0 ? (
            <p className="text-sm text-gray-500">No roles configured for this relation type.</p>
          ) : (
            <div className="space-y-2">
              {(grouped[rt.value] || []).map(role => (
                <div key={role.id} className={`flex items-center gap-3 p-3 rounded-lg ${role.is_active ? 'bg-gray-50' : 'bg-gray-100 opacity-60'}`}>
                  <input
                    type="text"
                    defaultValue={role.role_name}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== role.role_name) handleUpdate(role, { role_name: v });
                    }}
                    className={cellInputClass}
                  />
                  <input
                    type="text"
                    placeholder="contact_type filter"
                    defaultValue={role.applies_to_contact_type || ''}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (role.applies_to_contact_type || '')) {
                        handleUpdate(role, { applies_to_contact_type: v || null });
                      }
                    }}
                    className="w-44 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  <input
                    type="number"
                    defaultValue={role.sort_order}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!Number.isNaN(v) && v !== role.sort_order) handleUpdate(role, { sort_order: v });
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-1 focus:ring-brand-500"
                    title="Sort order"
                  />
                  <button
                    onClick={() => handleDelete(role.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                    title="Deactivate"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
